import { parse, format, formatDistanceToNow } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

/**
 * Parses a date string or returns a Date object if already a Date
 * @param date Date string or Date object
 * @param formatStr Format string for parsing (default: "MM/dd/yyyy")
 * @returns Date object
 */
export const parseDate = (date: string | Date, formatStr: string = "MM/dd/yyyy"): Date => {
  if (typeof date === 'string') {
    // If it's already in YYYY-MM-DD format (from database), parse it safely
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return parseDateOnlyString(date);
    }
    // Otherwise use the provided format
    return parse(date, formatStr, new Date());
  }
  return date;
};

/**
 * Converts a date to an ISO string date (YYYY-MM-DD)
 * @param date Date string or Date object
 * @param formatStr Format string for parsing if date is a string (default: "MM/dd/yyyy")
 * @returns ISO date string (YYYY-MM-DD)
 */
export const toISODateString = (date: string | Date, formatStr: string = "MM/dd/yyyy"): string => {
  if (!date) return '';
  
  const dateObj = parseDate(date, formatStr);
  
  // Check if the date is valid before calling toISOString()
  if (isNaN(dateObj.getTime())) {
    console.warn('Invalid date provided to toISODateString:', date);
    return '';
  }
  
  return dateObj.toISOString().split('T')[0];
};

/**
 * Formats a date in a human-readable format
 * @param date Date string or Date object 
 * @param outputFormat Output format string (default: "MMMM d, yyyy")
 * @returns Formatted date string
 */
export const formatDisplayDate = (
  date: string | Date, 
  outputFormat: string = "MMMM d, yyyy"
): string => {
  if (!date) return "N/A";
  
  const dateObj = typeof date === 'string' 
    ? new Date(date) // Assume ISO format when directly parsing
    : date;
    
  return format(dateObj, outputFormat);
};

/**
 * Safely formats a timestamp for message display
 * Handles timezone conversion properly for database timestamps
 * @param timestamp ISO timestamp string from database
 * @returns Formatted relative time string (e.g. "2 minutes ago")
 */
export const formatMessageTime = (timestamp: string): string => {
  if (!timestamp) return "";
  
  try {
    // Parse the ISO timestamp - this will handle timezone conversion correctly
    const messageDate = new Date(timestamp);
    
    // Check if the date is valid
    if (isNaN(messageDate.getTime())) {
      console.warn("Invalid timestamp received:", timestamp);
      return "Invalid date";
    }
    
    return formatDistanceToNow(messageDate, { addSuffix: true });
  } catch (error) {
    console.error("Error formatting message time:", error, "timestamp:", timestamp);
    return "Unknown time";
  }
};

/**
 * Formats a timestamp for detailed message time display
 * @param timestamp ISO timestamp string from database
 * @returns Formatted time string (e.g. "2:30 PM")
 */
export const formatMessageTimeDetailed = (timestamp: string): string => {
  if (!timestamp) return "";
  
  try {
    const messageDate = new Date(timestamp);
    
    if (isNaN(messageDate.getTime())) {
      console.warn("Invalid timestamp received:", timestamp);
      return "Invalid time";
    }
    
    return format(messageDate, 'h:mm a');
  } catch (error) {
    console.error("Error formatting detailed message time:", error, "timestamp:", timestamp);
    return "Unknown time";
  }
};

/**
 * Formats a timestamp for full message timestamp display (tooltip)
 * @param timestamp ISO timestamp string from database
 * @returns Formatted full timestamp string (e.g. "June 9, 2025, 2:30 PM")
 */
export const formatMessageTimeFull = (timestamp: string): string => {
  if (!timestamp) return "";
  
  try {
    const messageDate = new Date(timestamp);
    
    if (isNaN(messageDate.getTime())) {
      console.warn("Invalid timestamp received:", timestamp);
      return "Invalid timestamp";
    }
    
    return format(messageDate, 'MMMM d, yyyy, h:mm a');
  } catch (error) {
    console.error("Error formatting full message time:", error, "timestamp:", timestamp);
    return "Unknown timestamp";
  }
};

/**
 * Safely parses a date-only string from the database in local timezone
 * Fixes the issue where date-only strings are interpreted as UTC
 * @param dateString Date string in YYYY-MM-DD format from database
 * @returns Date object in local timezone
 */
export const parseDateOnlyString = (dateString: string): Date => {
  if (!dateString) return new Date();
  
  // Split the date string and create a new Date in local timezone
  const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
  
  // Date constructor: new Date(year, monthIndex, day)
  // Note: month is 0-indexed in Date constructor
  return new Date(year, month - 1, day);
};

/**
 * Formats a date for display in forms, handling database date strings properly
 * @param date Date string from database or Date object
 * @param outputFormat Format string (default: "MM/dd/yyyy")
 * @returns Formatted date string
 */
export const formatDateForForm = (date: string | Date, outputFormat: string = "MM/dd/yyyy"): string => {
  if (!date) return "";
  
  let dateObj: Date;
  if (typeof date === 'string') {
    // Check if it's a date-only string (YYYY-MM-DD format)
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      dateObj = parseDateOnlyString(date);
    } else {
      // Assume it's an ISO timestamp
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }
  
  return format(dateObj, outputFormat);
};

/**
 * Formats a date string in a specific timezone
 * @param dateString ISO datetime string (e.g., from database)
 * @param timezone IANA timezone string (e.g., "America/New_York")
 * @param formatString Output format string (default: "MMM d, yyyy 'at' h:mm a")
 * @returns Formatted date string in the specified timezone
 */
export const formatDateInTimezone = (
  dateString: string, 
  timezone: string, 
  formatString: string = "MMM d, yyyy 'at' h:mm a"
): string => {
  if (!dateString) return "";
  
  try {
    // Handle timestamps without timezone indicator - assume they are UTC
    let normalizedDateString = dateString;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?$/.test(dateString)) {
      // Add 'Z' suffix if missing to force UTC interpretation
      normalizedDateString = dateString + 'Z';
    }
    
    // Parse the UTC date from database
    const utcDate = new Date(normalizedDateString);
    
    if (isNaN(utcDate.getTime())) {
      console.warn("Invalid date string provided:", dateString);
      return "Invalid date";
    }
    
    // Format the UTC date directly in the target timezone
    return formatInTimeZone(utcDate, timezone, formatString);
  } catch (error) {
    console.error("Error formatting date in timezone:", error, "date:", dateString, "timezone:", timezone);
    // Fallback to basic formatting without timezone conversion
    return format(new Date(dateString), formatString);
  }
};

/**
 * Formats a payment date for display in payment history
 * @param dateString ISO datetime string from database (paid_at field)
 * @param timezone Organization timezone (optional, defaults to browser timezone)
 * @returns Formatted payment date string
 */
export const formatPaymentDate = (dateString: string, timezone?: string): string => {
  if (!timezone) {
    // Fallback to basic formatting if no timezone provided
    return format(new Date(dateString), "MMM d, yyyy 'at' h:mm a");
  }
  
  return formatDateInTimezone(dateString, timezone, "MMM d, yyyy 'at' h:mm a");
}; 